import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setApiClientToken, setApiClientTokenProvider, clearApiClientToken } from '@shared/apiClient';

const TokenContext = createContext({
  simpleToken: undefined,
  richToken: undefined,
  loading: true,
  getToken: () => undefined as string | null | undefined,
});

interface TokenProps {
  children: ReactNode;
}

export const TokenProvider = ({ children }: TokenProps) => {
  const { getAccessTokenSilently, getIdTokenClaims, isAuthenticated, isLoading: auth0Loading } = useAuth0();
  const [simpleToken, setSimpleToken] = useState(null);
  const [richToken, setRichToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const getToken = useCallback(() => simpleToken, [simpleToken]);

  const contextValue = useMemo(
    () => ({
      simpleToken,
      richToken,
      loading,
      getToken,
    }),
    [simpleToken, richToken, loading, getToken],
  );

  const fetchToken = useCallback(async () => {
    if (!isAuthenticated) {
      setSimpleToken(null);
      setRichToken(null);
      clearApiClientToken();
      setLoading(false);
      return;
    }

    try {
      const accessToken = await getAccessTokenSilently();
      setSimpleToken(accessToken);
      setApiClientToken(accessToken);

      const claims = await getIdTokenClaims();
      // eslint-disable-next-line no-underscore-dangle
      setRichToken(claims?.__raw);
    } catch (error) {
      setSimpleToken(null);
      setRichToken(null);
      clearApiClientToken();
    }
    setLoading(false);
  }, [getAccessTokenSilently, getIdTokenClaims, isAuthenticated]);

  useEffect(() => {
    if (!auth0Loading) {
      fetchToken();
    }
  }, [fetchToken, auth0Loading]);

  useEffect(() => {
    let silentAuthFailed = false;

    setApiClientTokenProvider(async () => {
      if (!isAuthenticated || silentAuthFailed) return undefined;

      try {
        return await getAccessTokenSilently();
      } catch (error) {
        silentAuthFailed = true;
        return undefined;
      }
    });

    return () => setApiClientTokenProvider(undefined);
  }, [getAccessTokenSilently, isAuthenticated]);

  return <TokenContext.Provider value={contextValue}>{children}</TokenContext.Provider>;
};

export const useAuth0Token = () => useContext(TokenContext);
